/**
 * WebSocket context for managing a single WebSocket connection across the app.
 */
import React, { createContext, useContext, useCallback, useRef, useState, useEffect } from 'react';
import { config } from '../config';

interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

interface WebSocketContextType {
  isConnected: boolean;
  connectionError: string | null;
  send: (message: WebSocketMessage) => void;
  subscribe: (entityType: string, entityId: string) => void;
  unsubscribe: (entityType: string, entityId: string) => void;
  addMessageListener: (listener: (message: WebSocketMessage) => void) => () => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const ws = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef<NodeJS.Timer | null>(null);
  const subscriptionsRef = useRef<Set<string>>(new Set());
  const messageListeners = useRef<Set<(message: WebSocketMessage) => void>>(new Set());
  const [lastMessageType, setLastMessageType] = useState<string | null>(null);

  const maxReconnectAttempts = 10;
  const reconnectInterval = 5000;

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setConnectionError('No authentication token found');
      return;
    }

    // Construct WebSocket URL - Use new reliable v2 endpoint
    // Remove /api from the end if present, since we'll add the full endpoint path
    const baseUrl = config.API_URL.replace(/\/api$/, '');
    const wsUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    const fullUrl = `${wsUrl}/api/ws/v2/signals?token=${encodeURIComponent(token)}`;

    try {
      // Prevent duplicate connections (helps with React StrictMode double-invoke)
      if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
        console.debug('[WebSocket] Skipping connect: existing socket state=', ws.current.readyState);
        return;
      }
      ws.current = new WebSocket(fullUrl);

      ws.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttemptsRef.current = 0;

        // Re-subscribe to previous subscriptions
        subscriptionsRef.current.forEach(subscription => {
          const [entityType, entityId] = subscription.split(':');
          console.log('[WebSocket] Re-subscribing to:', entityType, entityId);
          send({
            type: 'subscribe',
            entity_type: entityType,
            entity_id: entityId
          });
        });

        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000) as any;
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[WebSocket] Message received:', data.type, data);
          setLastMessageType(data?.type || null);
          // Basic acks visibility
          if (data?.type === 'subscribed' || data?.type === 'unsubscribed') {
            console.info('[WebSocket] Subscription ack:', data);
          }
          
          // Notify all listeners
          messageListeners.current.forEach(listener => {
            try {
              listener(data);
            } catch (error) {
              console.error('Error in WebSocket message listener:', error);
            }
          });
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionError('WebSocket connection error');
      };

      ws.current.onclose = (event) => {
        console.log('WebSocket disconnected', event.code, event.reason);
        setIsConnected(false);

        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }

        // Handle reconnection
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = reconnectInterval * Math.min(reconnectAttemptsRef.current, 3);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay) as any;
        } else {
          setConnectionError('Maximum reconnection attempts reached');
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      setConnectionError('Failed to establish WebSocket connection');
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
    subscriptionsRef.current.clear();
    setIsConnected(false);
  }, []);

  const send = useCallback((message: WebSocketMessage) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  }, []);

  const subscribe = useCallback((entityType: string, entityId: string) => {
    const key = `${entityType}:${entityId}`;
    subscriptionsRef.current.add(key);
    
    console.log('[WebSocket] Subscribing to:', entityType, entityId);

    send({
      type: 'subscribe',
      entity_type: entityType,
      entity_id: entityId
    });
  }, [send]);

  const unsubscribe = useCallback((entityType: string, entityId: string) => {
    const key = `${entityType}:${entityId}`;
    subscriptionsRef.current.delete(key);

    send({
      type: 'unsubscribe',
      entity_type: entityType,
      entity_id: entityId
    });
  }, [send]);

  const addMessageListener = useCallback((listener: (message: WebSocketMessage) => void) => {
    messageListeners.current.add(listener);
    
    // Return cleanup function
    return () => {
      messageListeners.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    connect();
    
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  const contextValue: WebSocketContextType = {
    isConnected,
    connectionError,
    send,
    subscribe,
    unsubscribe,
    addMessageListener
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
      {process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && localStorage.getItem('wsDebugBadge') === '1' && (
        <div style={{position:'fixed',right:8,bottom:8,background:'rgba(67,66,255,0.9)',color:'#fff',padding:'6px 10px',borderRadius:8,fontSize:11,zIndex:9999,boxShadow:'0 2px 6px rgba(0,0,0,0.2)'}}>
          <div>WS: {isConnected ? 'connected' : 'disconnected'}</div>
          <div>Subs: {subscriptionsRef.current.size}</div>
          <div>Last: {lastMessageType || '-'}</div>
        </div>
      )}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

// Legacy hook for backwards compatibility
// Legacy hook removed — migrate to useWebSocket directly.
